import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Compass, Lock } from "lucide-react";
import { Logo } from "../shared/Logo";

/**
 * Stylised "live preview" of a Klareet glimpse, used in the hero right column.
 * It's not a real glimpse — it's a static teaser. All numbers are deliberate.
 */
export function HeroPreview() {
    const score = 76;
    const radius = 38;
    const circumference = 2 * Math.PI * radius;
    const [dashOffset, setDashOffset] = useState(circumference);

    useEffect(() => {
        const id = requestAnimationFrame(() =>
            setDashOffset(circumference * (1 - score / 100)),
        );
        return () => cancelAnimationFrame(id);
    }, [circumference]);

    const bars = [
        { label: "Curiosity", value: 82 },
        { label: "Discipline", value: 64 },
        { label: "Adaptability", value: 71 },
    ];

    return (
        <div
            className="relative w-full max-w-[480px] mx-auto"
            data-testid="hero-preview"
        >
            {/* Soft teal blob behind the card */}
            <div
                className="absolute -inset-10 -z-10 pointer-events-none rounded-[40px]"
                style={{
                    background:
                        "radial-gradient(70% 65% at 60% 40%, rgba(20,184,166,0.45), rgba(13,148,136,0) 70%)",
                    filter: "blur(40px)",
                    opacity: 0.55,
                }}
                aria-hidden
            />

            <motion.div
                initial={{ opacity: 0, y: 16, rotate: -2 }}
                animate={{
                    opacity: 1,
                    y: [-4, 4, -4],
                    rotate: -2,
                }}
                transition={{
                    opacity: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] },
                    y: { duration: 5, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 0.5 },
                }}
                className="relative rounded-3xl border border-klareet-bor bg-gradient-to-br from-klareet-CARD to-[#080d1a] p-6 md:p-7 shadow-[0_30px_80px_-20px_rgba(20,184,166,0.25)] lg:rotate-[-2deg]"
            >
                {/* Mock header */}
                <div className="flex items-center justify-between">
                    <Logo className="h-7 w-auto" />
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-klareet-MUT">
                        Glimpse
                    </span>
                </div>

                {/* Archetype mini card */}
                <div className="mt-5 rounded-2xl border border-klareet-bor bg-klareet-card/80 p-4 relative overflow-hidden">
                    <div
                        className="absolute -top-12 -right-10 w-32 h-32 rounded-full blur-3xl bg-klareet-T/30 pointer-events-none"
                        aria-hidden
                    />
                    <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-klareet-MUT">
                        Your archetype
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                        <span className="inline-flex w-9 h-9 rounded-full bg-klareet-T/20 items-center justify-center">
                            <Compass size={18} className="text-klareet-T2" />
                        </span>
                        <span className="text-2xl font-semibold tracking-tight text-klareet-T2">
                            EXPLORER
                        </span>
                    </div>
                </div>

                {/* Score + bars row */}
                <div className="mt-5 grid grid-cols-[auto_1fr] gap-4 items-center">
                    {/* Score gauge */}
                    <div className="relative w-[100px] h-[100px]">
                        <svg width="100" height="100" className="-rotate-90">
                            <circle
                                cx="50"
                                cy="50"
                                r={radius}
                                stroke="#1A2540"
                                strokeWidth="10"
                                fill="transparent"
                            />
                            <circle
                                cx="50"
                                cy="50"
                                r={radius}
                                stroke="#10B981"
                                strokeWidth="10"
                                fill="transparent"
                                strokeLinecap="round"
                                strokeDasharray={circumference}
                                strokeDashoffset={dashOffset}
                                style={{
                                    transition:
                                        "stroke-dashoffset 1.5s cubic-bezier(.2,.8,.2,1)",
                                }}
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="font-mono text-2xl font-semibold tabular-nums text-klareet-GREEN">
                                {score}
                            </span>
                            <span className="text-[10px] text-klareet-MUT font-mono">
                                / 100
                            </span>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {bars.map((b, i) => (
                            <BarRow key={b.label} {...b} delay={250 + i * 120} />
                        ))}
                    </div>
                </div>

                {/* Locked row */}
                <div className="mt-5 relative rounded-xl border border-klareet-bor bg-klareet-card/60 p-3">
                    <div
                        className="select-none"
                        style={{ filter: "blur(4px)" }}
                        aria-hidden
                    >
                        <div className="flex items-center justify-between text-[11px] text-klareet-MUT mb-1">
                            <span>Emotional patterns</span>
                            <span className="font-mono">68</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-klareet-BOR overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-klareet-T to-klareet-T2 rounded-full"
                                style={{ width: "68%" }}
                            />
                        </div>
                    </div>
                    <div className="absolute inset-0 grid place-items-center">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-klareet-bor bg-klareet-NAVY text-klareet-MUT text-[10px] font-mono">
                            <Lock size={11} />
                            Unlocks at launch
                        </span>
                    </div>
                </div>

                {/* Week 1 strip */}
                <div className="mt-4 rounded-xl border border-klareet-T/40 bg-klareet-T/10 p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-klareet-T2">
                        Week 1
                    </div>
                    <div className="mt-1 text-[12.5px] text-klareet-TEXT leading-snug">
                        Write the three career outcomes you'd pick if no one
                        judged you. No editing.
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

function BarRow({ label, value, delay }) {
    const [w, setW] = useState(0);
    useEffect(() => {
        const t = setTimeout(() => setW(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return (
        <div>
            <div className="flex items-center justify-between text-[11px] text-klareet-MUT mb-1">
                <span>{label}</span>
                <span className="font-mono tabular-nums text-klareet-TEXT">
                    {value}
                </span>
            </div>
            <div className="h-1.5 rounded-full bg-klareet-BOR overflow-hidden">
                <div
                    className="h-full bg-gradient-to-r from-klareet-T to-klareet-T2 rounded-full transition-[width] duration-[900ms] ease-[cubic-bezier(.2,.8,.2,1)]"
                    style={{ width: `${w}%` }}
                />
            </div>
        </div>
    );
}
